import { ChangeDetectionStrategy, Component, effect, inject, linkedSignal, signal } from '@angular/core';
import { FormField, form, min, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Observable, finalize } from 'rxjs';
import { Messenger, ProductService } from 'src/app/service';

import { Milestone, Project } from '../schemas';

export interface MilestoneFormData {
  item?: Milestone;
  title?: string;
  projects: Project[];
  ownerId: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormField,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressBarModule,
    MatDatepickerModule,
  ],
  templateUrl: './milestone-form.html',
})
export class MilestoneForm {
  data = (inject(MAT_DIALOG_DATA) as MilestoneFormData) || {};

  hostDialog = inject(MatDialogRef);
  messenger = inject(Messenger);

  productService = inject(ProductService);

  pending = signal(false);

  currencies = ['AUD', 'EURO', 'USD', 'NZD', 'CAD'];

  projects = linkedSignal(() => this.data.projects).asReadonly();

  milestoneStatuses = [
    [0, 'Not ready to invoice'],
    [10, 'Ready to invoice'],
    [20, 'Invoiced'],
  ];

  model = linkedSignal(() => {
    return {
      Id: '',
      Name: '',
      DueDate: '',
      Status: 0,
      HourlyRate: this.data.item ? 175 : 195,
      Value: 0,
      Currency: 'AUD',
      Rate: 1,
      Notes: '',
      ProjectId: '',
      PurchaseOrder: '',
      ...this.data.item,
    } as Milestone;
  });

  form = form(this.model, (schema) => {
    required(schema.Name);
    required(schema.DueDate);
    required(schema.Status);
    required(schema.HourlyRate);
    min(schema.HourlyRate, 0);
    required(schema.Value);
    required(schema.Currency);
    required(schema.Rate);
    min(schema.Rate, 0);
  });

  constructor() {
    effect(() => {
      const project = this.form.ProjectId().value();
      if (!project) return;

      const existing = this.projects().find(({ Id }) => Id === project);
      if (existing) {
        this.form.PurchaseOrder().value.set(existing.PurchaseOrder);
      }
    });
  }

  save() {
    this.pending.set(true);

    const data = this.form().value();

    let req$: Observable<unknown>;
    if (data.Id) {
      req$ = this.productService.updateMilestone(this.data.ownerId, data);
    } else {
      req$ = this.productService.addMilestone(this.data.ownerId, data);
    }

    req$.pipe(finalize(() => this.pending.set(false))).subscribe({
      next: () => this.hostDialog.close(true),
      error: this.messenger.handleHttpError,
    });
  }
}
